// cookie_library.ts
import { getRootDomain } from '@/core/event_logic/utils/url_utils';

type CookieAttributes = {
  [property: string]: unknown;
  domain?: string;
  expires?: Date | number;
  path?: string;
  secure?: boolean;
};

class CookieLibrary {
  /**
   * In-memory store for cookies
   *
   */
  private static cookieStore: Record<string, string> = {};

  /**
   * Sets a cookie with the specified key, value, and options.
   *
   * @param key - The key for the cookie.
   * @param value - The value for the cookie.
   * @param options - Additional cookie attributes.
   */
  public static set(
    key: string,
    value: string,
    options: CookieAttributes = {},
  ): void {
    let cookieString = `${encodeURIComponent(key)}=${encodeURIComponent(
      value,
    )}`;

    if (options.expires) {
      const expires = this.getExpiresString(options.expires);
      cookieString += `; expires=${expires}`;
    }

    if (options.path) {
      cookieString += `; path=${options.path}`;
    }

    // make sure the cookie is accessible by all subdomains
    if (location.hostname != 'localhost') {
      let cookieDomain = options.domain || getRootDomain(location.hostname);
      if (!cookieDomain.startsWith('.')) cookieDomain = '.' + cookieDomain;
      cookieString += `; domain=${cookieDomain}`;
    }

    if (options.secure) {
      cookieString += `; secure`;
    }

    // eslint-disable-next-line unicorn/no-document-cookie
    document.cookie = cookieString;
    this.cookieStore[key] = value;
  }

  /**
   * Gets the value of a cookie with the specified key.
   *
   * @param key - The key for the cookie.
   * @returns The value of the cookie, or null if not found.
   */
  public static get(key: string): string | null {
    if (this.cookieStore[key]) return this.cookieStore[key];

    const cookies = document.cookie ? document.cookie.split('; ') : [];
    for (const cookie of cookies) {
      const [cookieKey, cookieValue] = cookie.split('=');
      if (cookieKey && cookieValue && decodeURIComponent(cookieKey) === key) {
        this.cookieStore[key] = decodeURIComponent(cookieValue);
        return this.cookieStore[key];
      }
    }

    return null;
  }

  /**
   * Gets the value of a cookie with the specified key.
   *
   * @param key - The key for the cookie.
   * @returns The value of the cookie, or null if not found.
   */
  public static getAll(): Record<string, unknown> {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    const cookie_key_values: Record<string, unknown> = {};
    for (const cookie of cookies) {
      const [cookieKey, cookieValue] = cookie.split('=');
      if (cookieKey && cookieValue) {
        cookie_key_values[decodeURIComponent(cookieKey)] =
          decodeURIComponent(cookieValue);
      }
    }
    return cookie_key_values;
  }

  /**
   * Removes a cookie with the specified key and options.
   *
   * @param key - The key for the cookie.
   * @param options - Additional cookie attributes.
   */
  public static remove(key: string, options: CookieAttributes = {}): void {
    this.set(key, '', { ...options, expires: -1 });
    delete this.cookieStore[key];
  }

  /**
   * Converts the expires option to a string.
   *
   * @param expires - The expires option.
   * @returns The expires string.
   */
  private static getExpiresString(expires: Date | number): string {
    if (typeof expires === 'number') {
      const date = new Date();
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const one_day_in_ms = 24 * 60 * 60 * 1000;
      date.setTime(date.getTime() + expires * one_day_in_ms);
      return date.toUTCString();
    }
    return expires.toUTCString();
  }

  /**
   * Clear cookie memory cache and document cookie. ONLY for Testing purposes
   */
  public static clearAllCookies() {
    this.cookieStore = {};
    for (let cookie in this.getAll()) this.remove(cookie);
  }
}

export { CookieAttributes, CookieLibrary };
